/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { DatePicker, Button, Typography, message, Skeleton, Table } from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { formatIndianNumber } from "../../../../utils";
import { DirectorDashboardService3 } from "../../../../services/api/directorDashboardService3";
import axios from "axios";
import "../ZoneWiseView3/style.scss";
import "../style.scss";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const getDefaultDateRange = () => {
    const yesterday = moment().subtract(1, "day");
    return [yesterday, yesterday];
};

const HubOpsZoneWiseView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDateRange, setSelectedDateRange] = useState(getDefaultDateRange);
    const [loading, setLoading] = useState(false);
    const [zoneWiseData, setZoneWiseData] = useState([]);

    const getDisplayDate = () => {
        const [from, to] = selectedDateRange;
        const fromStr = from.format("DD/MM/YYYY");
        const toStr = to.format("DD/MM/YYYY");
        return fromStr === toStr ? fromStr : `${fromStr} - ${toStr}`;
    };

    const fetchData = async (dateRange) => {
        const [from, to] = dateRange;
        if (!from || !to) return;

        setLoading(true);
        const fromDate = from.format("YYYY-MM-DD");
        const toDate = to.format("YYYY-MM-DD");
        try {
            const response = await DirectorDashboardService3.getZoneWiseBreakdown(fromDate, toDate);
            if (response?.data?.data && response.data.data.length > 0) {
                setZoneWiseData(response.data.data.map((item, idx) => ({
                    key: idx,
                    zone: item.zone,
                    hub_inscan_shipments: item.hub_inscan_shipments || 0,
                    hub_inscan_bags: item.hub_inscan_bags || 0,
                    hub_outscan_shipments: item.hub_outscan_shipments || 0,
                    hub_outscan_bags: item.hub_outscan_bags || 0,
                    hub_bags_finalized: item.hub_bags_finalized || 0,
                })));
            } else {
                setZoneWiseData([]);
                message.warning("No data available for the selected date range");
            }
        } catch (error) {
            if (axios.isCancel(error)) return;
            console.error("Error fetching zone-wise breakdown:", error);
            message.error(error?.message || "Failed to load zone-wise data");
            setZoneWiseData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let from = moment().subtract(1, "day");
        let to = moment().subtract(1, "day");

        if (location.state?.date) {
            const start = moment(location.state.date, "YYYY-MM-DD", true);
            if (start.isValid()) {
                from = start;
                if (location.state?.endDate) {
                    const end = moment(location.state.endDate, "YYYY-MM-DD", true);
                    to = end.isValid() ? end : start;
                } else {
                    to = start;
                }
            }
        }

        const range = [from, to];
        setSelectedDateRange(range);
        fetchData(range);
    }, [location.state?.date, location.state?.endDate]);

    const handleDateSubmit = () => {
        fetchData(selectedDateRange);
    };

    const handleGoBack = () => {
        navigate("/director-dashboardNew2");
    };

    const columns = [
        {
            title: "Zone",
            dataIndex: "zone",
            key: "zone",
            fixed: "left",
            width: 120,
            render: (val) => <strong style={{ color: "#132249" }}>{val}</strong>,
        },
        {
            title: "Hub Inscan Shipments",
            dataIndex: "hub_inscan_shipments",
            key: "hub_inscan_shipments",
            align: "right",
            render: (val) => formatIndianNumber(val),
        },
        {
            title: "Hub Inscan Bags",
            dataIndex: "hub_inscan_bags",
            key: "hub_inscan_bags",
            align: "right",
            render: (val) => formatIndianNumber(val),
        },
        {
            title: "Hub Outscan Shipments",
            dataIndex: "hub_outscan_shipments",
            key: "hub_outscan_shipments",
            align: "right",
            render: (val) => formatIndianNumber(val),
        },
        {
            title: "Hub Outscan Bags",
            dataIndex: "hub_outscan_bags",
            key: "hub_outscan_bags",
            align: "right",
            render: (val) => formatIndianNumber(val),
        },
        {
            title: "Bags Finalized",
            dataIndex: "hub_bags_finalized",
            key: "hub_bags_finalized",
            align: "right",
            render: (val) => formatIndianNumber(val),
        },
    ];

    const summaryRow = (pageData) => {
        const total = pageData.reduce(
            (acc, row) => ({
                hub_inscan_shipments: acc.hub_inscan_shipments + row.hub_inscan_shipments,
                hub_inscan_bags: acc.hub_inscan_bags + row.hub_inscan_bags,
                hub_outscan_shipments: acc.hub_outscan_shipments + row.hub_outscan_shipments,
                hub_outscan_bags: acc.hub_outscan_bags + row.hub_outscan_bags,
                hub_bags_finalized: acc.hub_bags_finalized + row.hub_bags_finalized,
            }),
            { hub_inscan_shipments: 0, hub_inscan_bags: 0, hub_outscan_shipments: 0, hub_outscan_bags: 0, hub_bags_finalized: 0 }
        );
        return (
            <Table.Summary.Row style={{ background: "#eef1f8", fontWeight: 700 }}>
                <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right"><strong>{formatIndianNumber(total.hub_inscan_shipments)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right"><strong>{formatIndianNumber(total.hub_inscan_bags)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><strong>{formatIndianNumber(total.hub_outscan_shipments)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><strong>{formatIndianNumber(total.hub_outscan_bags)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right"><strong>{formatIndianNumber(total.hub_bags_finalized)}</strong></Table.Summary.Cell>
            </Table.Summary.Row>
        );
    };

    return (
        <div className="director-dashboard zone-wise-page hub-ops-zone-wise-page">
            <div className="zone-wise-header">
                <div className="header-left">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleGoBack}
                        className="back-button"
                    />
                    <Title level={4} className="page-title">
                        Hub Operations — Zone Wise Breakdown &nbsp;
                        <span style={{ fontSize: 14, fontWeight: 400, color: "#8c8c8c" }}>
                            {getDisplayDate()}
                        </span>
                    </Title>
                </div>

                <div className="header-right">
                    <div className="zone-wise-date-filter">
                        <span className="zone-wise-date-label">Date Range</span>
                        <RangePicker
                            format="DD/MM/YYYY"
                            value={selectedDateRange}
                            onChange={(dates) => {
                                if (dates && dates[0] && dates[1]) {
                                    setSelectedDateRange([dates[0], dates[1]]);
                                } else {
                                    setSelectedDateRange(getDefaultDateRange());
                                }
                            }}
                            className="zone-wise-date-picker hub-ops-range-picker"
                            allowClear={false}
                            disabledDate={(current) => current && current >= moment().startOf("day")}
                        />
                        <Button type="primary" onClick={handleDateSubmit} className="zone-wise-submit-btn" icon={<ReloadOutlined />}>
                            Submit
                        </Button>
                    </div>
                </div>
            </div>

            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden", marginTop: 8 }}>
                {loading ? (
                    <div style={{ padding: 24 }}>
                        {[...Array(7)].map((_, i) => (
                            <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: 12 }} />
                        ))}
                    </div>
                ) : (
                    <Table
                        dataSource={zoneWiseData}
                        columns={columns}
                        pagination={false}
                        scroll={{ x: true }}
                        size="middle"
                        className="zone-wise-table"
                        summary={summaryRow}
                        style={{ borderRadius: 12 }}
                    />
                )}
            </div>
        </div>
    );
};

export default HubOpsZoneWiseView;
